import type { Order, Party, Product, Session } from '@prody/shared';
import type {
  AssignSupplierInput,
  ListParams,
  NewOrderInput,
  ProdyApi,
} from './api';

export type HttpApiOptions = {
  baseUrl: string;
  /** Fallback for methods not yet on Nest (quotes, logistics, …). */
  fallback: ProdyApi;
};

class HttpError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'HttpError';
  }
}

async function request<T>(
  baseUrl: string,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const res = await fetch(`${baseUrl}${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new HttpError(res.status, text || res.statusText);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

const qs = (params: Record<string, string | number | undefined | null>): string => {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
};

/**
 * Nest-backed ProdyApi. Session / parties / products / orders hit the
 * API; everything else delegates to `fallback` (usually the mock) so
 * the UI keeps working while domains land.
 */
export const createHttpApi = ({ baseUrl, fallback }: HttpApiOptions): ProdyApi => {
  const root = baseUrl.replace(/\/$/, '');

  return {
    getSession: () => request<Session>(root, '/api/session'),

    listOrders: async (businessId, filters = {}) => {
      const sp = new URLSearchParams();
      if (filters.deliveryDate) sp.set('deliveryDate', filters.deliveryDate);
      if (filters.customerId) sp.set('customerId', filters.customerId);
      if (filters.search) sp.set('search', filters.search);
      if (filters.limit) sp.set('limit', String(filters.limit));
      for (const s of filters.statuses ?? []) sp.append('status', s);
      const q = sp.toString();
      return request<Order[]>(
        root,
        `/api/businesses/${businessId}/orders${q ? `?${q}` : ''}`,
      );
    },

    getOrder: (businessId, orderId) =>
      request<Order | null>(root, `/api/businesses/${businessId}/orders/${orderId}`),

    createOrder: (businessId, input: NewOrderInput) =>
      request<Order>(root, `/api/businesses/${businessId}/orders`, {
        method: 'POST',
        body: JSON.stringify({
          ...input,
          lines: input.lines.map((l) => ({
            productId: l.productId,
            quantity: l.quantity,
            unitPrice: l.unitPrice,
            priceSource: l.priceSource,
            note: l.note ?? null,
            taxRateKey: 'reduced',
          })),
        }),
      }),

    updateOrder: (businessId, orderId, patch) =>
      request<Order>(root, `/api/businesses/${businessId}/orders/${orderId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          status: patch.status,
          deliveryDate: patch.deliveryDate,
          customerNote: patch.customerNote,
          internalNote: patch.internalNote,
        }),
      }),

    assignSupplier: (businessId, input: AssignSupplierInput) =>
      fallback.assignSupplier(businessId, input),
    generatePurchaseTasks: (businessId, tradingDay) =>
      fallback.generatePurchaseTasks(businessId, tradingDay),

    listQuotes: (businessId, tradingDay) => fallback.listQuotes(businessId, tradingDay),
    updateQuote: (businessId, quoteId, patch) =>
      fallback.updateQuote(businessId, quoteId, patch),

    listPurchaseTasks: (businessId, tradingDay) =>
      fallback.listPurchaseTasks(businessId, tradingDay),
    updatePurchaseTask: (businessId, taskId, patch) =>
      fallback.updatePurchaseTask(businessId, taskId, patch),

    listShipments: (businessId, date) => fallback.listShipments(businessId, date),
    updateShipment: (businessId, shipmentId, patch) =>
      fallback.updateShipment(businessId, shipmentId, patch),
    listRoutes: (businessId, date) => fallback.listRoutes(businessId, date),

    listProducts: (businessId, params?: ListParams) =>
      request<Product[]>(
        root,
        `/api/businesses/${businessId}/products${qs({
          search: params?.search,
          limit: params?.limit,
        })}`,
      ),
    getProduct: (businessId, productId) =>
      request<Product | null>(root, `/api/businesses/${businessId}/products/${productId}`),

    listParties: (businessId, params?: ListParams & { role?: string }) =>
      request<Party[]>(
        root,
        `/api/businesses/${businessId}/parties${qs({
          search: params?.search,
          role: params?.role,
          limit: params?.limit,
        })}`,
      ),
    getParty: (businessId, partyId) =>
      request<Party | null>(root, `/api/businesses/${businessId}/parties/${partyId}`),

    listStock: (businessId, params) => fallback.listStock(businessId, params),
    listInvoices: (businessId, params) => fallback.listInvoices(businessId, params),
    listPayments: (businessId, params) => fallback.listPayments(businessId, params),
    listBalances: (businessId, direction) => fallback.listBalances(businessId, direction),
    listThreads: (businessId, params) => fallback.listThreads(businessId, params),
    listMessages: (businessId, threadId) => fallback.listMessages(businessId, threadId),
    sendMessage: (businessId, threadId, body, channel) =>
      fallback.sendMessage(businessId, threadId, body, channel),
    listAppointments: (businessId, from, to) =>
      fallback.listAppointments(businessId, from, to),
    listTeam: (businessId) => fallback.listTeam(businessId),
  };
};
